import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Drawer, IconButton, Typography } from '@mui/material'
import { IconChevronDown, IconX } from '@tabler/icons-react'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { CheckboxMultiple } from '~/components/Form/CheckboxMultiple'
import { Input } from '~/components/Form/Input'
import { SearchQueysData, useQueryString } from '~/hooks'
import { contractInvoiceOptions, invoicePaymentTypeOptions } from '~/utils/options'
import { EInvoiceStatus } from '~/graphql/types'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

type FilterData = {
  status?: string[],
  contractId?: string,
  invoiceId?: string,
  customerName?: string,
  customerEmail?: string,
  customerDocument?: string,
  begin?: string,
  end?: string
}

type FilterInvoiceDrawerProps = {
  drawerIsOpen: boolean
  setDrawerIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  toggleDrawer: () => void
  queryKeys: SearchQueysData[]
}

const schema = yup
  .object({
    begin: yup.string().test('isValid', (value, validation) => {

      if (value && Boolean(!validation.parent.end)) {
        return validation.createError({
          message: 'Este campo é obrigatório.',
          path: 'end'
        })
      }
      return true
    }),
    end: yup.string()
      .test('isValid', (value, validation) => {
        if (value && Boolean(!validation.parent.begin)) {
          return validation.createError({
            message: 'Este campo é obrigatório.',
            path: 'begin'
          })
        }
        return true
      }),
  })
  .required()

export const FilterInvoiceDrawer: React.FC<FilterInvoiceDrawerProps> = ({ drawerIsOpen, toggleDrawer, setDrawerIsOpen, queryKeys }) => {
  const formMethods = useForm({
    resolver: yupResolver(schema)
  })

  const { searchQuerys, setSearchParams } = useQueryString()
  const queryValues = searchQuerys(queryKeys)

  useEffect(() => {
    if (drawerIsOpen) {
      formMethods.setValue('begin', queryValues.begin)
      formMethods.setValue('end', queryValues.end)
      formMethods.setValue('status', queryValues.status)
      formMethods.setValue('invoiceId', queryValues.invoiceId)
      formMethods.setValue('contractId', queryValues.contractId)
      formMethods.setValue('customerName', queryValues.customerName)
      formMethods.setValue('customerEmail', queryValues.customerEmail)
      formMethods.setValue('customerName', queryValues.customerName)
      formMethods.setValue('paymentTypes', queryValues.paymentTypes)
    }
  }, [drawerIsOpen])

  const handleFilters = (filters: FilterData & { paymentTypes?: string[] }) => {
    setDrawerIsOpen(false)
    setSearchParams(state => {
      state.set('page', '0')
      if (filters?.status && filters?.status?.length > 0 || queryValues?.status?.length > 0) {
        state.set('status', JSON.stringify(filters.status) || queryValues.status)
      } else {
        state.delete('status')
      }
      filters.customerName ? state.set('customerName', filters.customerName || queryValues.customerName) : state.delete('customerName')
      filters.customerEmail ? state.set('customerEmail', filters.customerEmail || queryValues.customerEmail) : state.delete('customerEmail')
      filters.customerDocument ? state.set('customerDocument', filters.customerDocument || queryValues.customerDocument) : state.delete('customerDocument')
      filters.invoiceId ? state.set('invoiceId', filters.invoiceId || queryValues.invoiceId) : state.delete('invoiceId')
      filters.contractId ? state.set('contractId', filters.contractId || queryValues.contractId) : state.delete('contractId')
      filters.begin ? state.set('begin', filters.begin || queryValues.begin) : state.delete('begin')
      filters.end ? state.set('end', filters.end || queryValues.end) : state.delete('end')
      const hasPaidStatus = (filters?.status || queryValues.status || [])?.includes(EInvoiceStatus.paid as unknown as string)
      if (hasPaidStatus && (filters?.paymentTypes?.length || queryValues?.paymentTypes?.length)) {
        state.set('paymentTypes', JSON.stringify(filters?.paymentTypes || queryValues.paymentTypes))
      } else {
        state.delete('paymentTypes')
      }
      return state
    })
  }

  const onRemoveFilters = () => {
    setDrawerIsOpen(false)
    setSearchParams(state => {
      state.set('page', '0')
      state.delete('customerName')
      state.delete('customerEmail')
      state.delete('customerDocument')
      state.delete('invoiceId')
      state.delete('contractId')
      state.delete('status')
      state.delete('begin')
      state.delete('end')
      state.delete('paymentTypes')
      return state
    })
  }

  return (
    <Drawer PaperProps={{ sx: { maxWidth: '560px', width: '100%', padding: '2rem' } }} anchor='right' open={drawerIsOpen} onClose={toggleDrawer}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography fontWeight={500} variant='h5'>Filtrar por</Typography>
        <IconButton type='button' onClick={() => toggleDrawer()}>
          <IconX />
        </IconButton>
      </Box>

      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit((formData) => handleFilters({ ...formData }))}>
          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<IconChevronDown />} sx={{ padding: '0' }}>
              <Typography sx={{ paddingTop: '1rem' }} fontWeight={500}>Vencimento</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0 0 1rem 0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '.5rem 0 1rem 0' }}>
                <Input name='begin' type='date' />
                <Typography>Até</Typography>
                <Input name='end' type='date' />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<IconChevronDown />} sx={{ padding: '0' }}>
              <Typography sx={{ paddingTop: '1rem' }} fontWeight={500}>Fatura</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0 0 1rem 0' }}>
              <Box sx={{ padding: '1rem 0' }}>
                <Input label='ID da Fatura' name='invoiceId' />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<IconChevronDown />} sx={{ padding: '0' }}>
              <Typography sx={{ paddingTop: '1rem' }} fontWeight={500}>Contrato</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0 0 1rem 0' }}>
              <Box sx={{ padding: '1rem 0' }}>
                <Input label='ID do contrato' name='contractId' />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<IconChevronDown />} sx={{ padding: '0' }}>
              <Typography sx={{ paddingTop: '1rem' }} fontWeight={500}>Cliente</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0 0 1rem 0' }}>
              <Box sx={{ padding: '1rem 0', flexFlow: 'column', display: 'flex', gap: '1rem' }}>
                <Input label='Nome do cliente' name='customerName' />
                <Input label='E-mail do cliente' name='customerEmail' />
                <Input mask='cpfCnpj' label='Documento do cliente' name='customerDocument' />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<IconChevronDown />} sx={{ padding: '0', paddingTop: '1rem' }}>
              <Typography sx={{ padding: '0' }} fontWeight={500}>Status</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0 0 1rem 0' }}>
              <CheckboxMultiple name='status' options={contractInvoiceOptions} />
              {(formMethods.watch('status') || [])?.includes(EInvoiceStatus.paid as unknown as string) && (
                <Box sx={{ paddingTop: '1rem' }}>
                  <Typography sx={{ paddingTop: '0' }} fontWeight={500}>Tipos de pagamento</Typography>
                  <CheckboxMultiple name='paymentTypes' options={invoicePaymentTypeOptions} />
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          <Box sx={{ display: 'flex', padding: '2rem 0', gap: '1rem', justifyContent: 'flex-end' }}>
            <Button onClick={() => onRemoveFilters()} type='button' fullWidth color='secondary'>Limpar</Button>
            <Button type='submit' fullWidth color='primary'>Aplicar</Button>
          </Box>
        </form>
      </FormProvider>
    </Drawer>
  )
}
